import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./Style/Test.css";
import Loading from "../../Common/Loading";

import exit_n from '../Images/exit_n.png';
import timer_icon from '../Images/timer_icon.png';
import skip_n from '../Images/skip_n.png';
import next_test from '../Images/next_test.png';

import OQns, { AnswerRef as OQnsRef } from "./OQns";
import RadioQns, { AnswerRef as RadioQnsRef } from "./RadioQns";
import ImageQns, { AnswerRef as ImageQnsRef } from "./ImageQns";
import WQns, { AnswerRef as WQnsRef } from "./WQns";
import { getQuestionTypeFromHtml } from './utils/questionTypeUtilsQuestion';
import DQns, { AnswerRef as DQnsRef } from "./DQns";
import BQns, { AnswerRef as BQnsRef } from "./BQns";
import NQns, { AnswerRef as NQnsRef } from "./NQns";
import IQns, { AnswerRef as IQnsRef } from "./IQns";
import CQns, { AnswerRef as CQnsRef } from "./CQns";
import MQns, { AnswerRef as MQnsRef } from "./MQns";
import LQns, { AnswerRef as LQnsRef } from "./LQns";

import FQns, { AnswerRef as FQnsRef } from "./FQns";

import { buildNextQuestionData, buildShuffledQuestionsOrder, saveQuestionPerformance, QuestionPerformancePayload } from '../../services/questionApi';

type QuestionComponentRef = FQnsRef | WQnsRef | IQnsRef | BQnsRef | NQnsRef | LQnsRef | CQnsRef | MQnsRef | DQnsRef | OQnsRef | RadioQnsRef | ImageQnsRef;
type QuestionAnswer = NonNullable<ReturnType<QuestionComponentRef['getAnswer']>>;
type SkipMetadata = Pick<QuestionPerformancePayload, 'noOfOptions' | 'optOrder'>;
type SkipMetadataRef = QuestionComponentRef & {
    getSkipMetadata?: () => SkipMetadata | null;
};

const Test: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    const [lessonMeta, setLessonMeta] = useState<any>(null);
    const [currentQuestionHtml, setCurrentQuestionHtml] = useState<string>("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [timeLeft, setTimeLeft] = useState<number | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const questionComponentRef = useRef<QuestionComponentRef>(null);
    const questionStartedAtRef = useRef<number | null>(null);
    const questionLoadStartedAtRef = useRef<number | null>(null);
    const questionLoadTimeRef = useRef(0);

    const search = window.location.search;
    const params = new URLSearchParams(search);
    const dicCodeParam = params.get("DicCode");
    const lsnTestCodeParam = params.get("LsnTestCode");

    const hwidRaw = params.get("hwid");
    let hwid: number | null = null;
    if (hwidRaw === "0") hwid = 0;
    else if (hwidRaw && hwidRaw !== "null") hwid = parseInt(hwidRaw);
    
    const url = process.env.REACT_APP_DOCUMENT_LINK ;
    const assetsBaseUrl = process.env.REACT_APP_ASSETS_BASE_URL;

    const showAlert = useCallback((message: string) => {
        alert(message);
    }, []);

    const getQuestionAnswerTime = useCallback(() => {
        if (questionStartedAtRef.current === null) return 0;
        return Math.max(0, Math.floor((Date.now() - questionStartedAtRef.current) / 1000));
    }, []);

    const getQuestionLoadTime = useCallback(() => questionLoadTimeRef.current, []);

    const clearCompletionCaches = useCallback(async () => {
        const schoolCode = sessionStorage.getItem('SchoolCode') || '';
        const familyCode = sessionStorage.getItem('FamilyCode') || '';
        const login = sessionStorage.getItem('Login') || '';
        if (!schoolCode || !familyCode || !login) return;

        try {
            await fetch(`${url}clear_cache/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    SchoolCode: schoolCode,
                    FamilyCode: familyCode,
                    Login: login,
                    Caches: ['all_user'],
                }),
            });
        } catch (error) {
            // The test result is already committed; do not prevent navigation
            // if cache invalidation is temporarily unavailable.
            console.error('Error clearing completion caches:', error);
        }
    }, [url]);

    useEffect(() => {
        const initializeTest = async () => {
            if (!dicCodeParam || !lsnTestCodeParam) {
                setError("Test identifiers (DicCode, LsnTestCode) not specified in URL.");
                setLoading(false);
                return;
            }
            const accessToken = localStorage.getItem("access_token");
            if (!accessToken) {
                setError("Authentication token not found. Please log in again.");
                setLoading(false);
                return;
            }
            try {
                const res = await fetch(`${url}initialize_test/`, {
                    method: "POST",
                    headers: { 
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${accessToken}`
                    },
                    body: JSON.stringify({ 
                        DICCode: dicCodeParam,
                        LsnTestCode: lsnTestCodeParam,
                        HWID: hwid,
                        UnitCode: sessionStorage.getItem('UnitCode') || '',
                        ChapterCode: sessionStorage.getItem('ChapterCode') || ''
                    }),
                });
                if (res.status === 401) throw new Error("Your session has expired. Please log in again.");
                if (!res.ok) {
                    const errorData = await res.json();
                    throw new Error(errorData.error || "Failed to initialize the test session.");
                }
                const data = await res.json();
                if (data.lessonMeta && data.lessonMeta.qns) {
                    setLessonMeta(data.lessonMeta);
                    setTotalPages(data.lessonMeta.qns.length);
                    setTimeLeft(data.lessonMeta.duration);
                } else {
                    throw new Error("Invalid metadata received from the server.");
                }
            } catch (e: any) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
        };
        initializeTest();
    }, [url, dicCodeParam, lsnTestCodeParam]);

    const handleTimeout = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        setLoading(true);
        setCurrentQuestionHtml("");

        // Let the loading state render before the timeout submission begins.
        await new Promise<void>(resolve => {
            requestAnimationFrame(() => resolve());
        });

        showAlert("You have run out of Time. Hand test in.");

        try {
            if (lessonMeta && lessonMeta.qns && lessonMeta.qns[currentPage - 1]) {
                const schoolCode = sessionStorage.getItem('SchoolCode') || '';
                const familyCode = sessionStorage.getItem('FamilyCode') || '';
                const login = sessionStorage.getItem('Login') || '';
                const chapterCode = sessionStorage.getItem('ChapterCode') || '';
                const unitCode = sessionStorage.getItem('UnitCode') || '';
                const currentSessionId = lessonMeta?.sessionId;

                if (schoolCode && currentSessionId) {
                    for (let questionIndex = currentPage - 1; questionIndex < lessonMeta.qns.length; questionIndex += 1) {
                        const skippedQuestion = lessonMeta.qns[questionIndex];
                        const isFinalSkippedQuestion = questionIndex === lessonMeta.qns.length - 1;
                        const payload: QuestionPerformancePayload = {
                            schoolCode: schoolCode,
                            familyCode: familyCode,
                            login: login,
                            dicCode: dicCodeParam || '',
                            lsnTestCode: lsnTestCodeParam || '',
                            sessionId: currentSessionId,
                            lsnCode: skippedQuestion.LsnCode,
                            qnNo: skippedQuestion.QnNo,
                            subNo: skippedQuestion.SubNo || 0,
                            qnType: skippedQuestion.QnType,
                            marks: 0,
                            noOfOptions: 0,
                            optOrder: "",
                            enteredCorrectAnswer: "QnSkipped",
                            enteredWrongAnswer: "",
                            enteredBlankAnswer: "",
                            solutionSeen: 'N',
                            clueSeen: 'N',
                            principleSeen: 'N',
                            questionLoadTime: 0,
                            questionAnswerTime: 0,
                            lastQnInd: skippedQuestion.LastQnInd,
                            nextQnData: buildNextQuestionData(lessonMeta.qns, questionIndex + 1),
                            dbgShuffledQnsOrder: buildShuffledQuestionsOrder(lessonMeta.qns),
                            varValues: skippedQuestion.VarValues || "",
                            scoringMethod: null,
                            allQuestionsCompleted: isFinalSkippedQuestion ? 'True' : 'False',
                            chapterCode: chapterCode,
                            unitCode: unitCode,
                            hwid: hwid,
                            testType: lessonMeta.testType
                        };

                        await saveQuestionPerformance(payload);
                    }
                }
            }
        } catch (err) {
            console.error("Error finalizing test on timeout:", err);
        } finally {
            navigate(
                `/STUDENT/Tutorials/Result?DicCode=${dicCodeParam}` +
                `&LsnTestCode=${lsnTestCodeParam}` +
                `&SessionID=${lessonMeta?.sessionId}&source=test`
            );
        }
    };

    useEffect(() => {
        if (!lessonMeta) return;

        const timerId = setInterval(() => {
            setTimeLeft(prev => {
                if (prev === null) return null;
                if (prev <= 0) return 0;
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timerId);
    }, [lessonMeta]);

    useEffect(() => {
        if (timeLeft === 0) {
            handleTimeout();
        }
    }, [timeLeft]);

    useEffect(() => {
        if (!lessonMeta) return;
        const fetchQuestionHtml = async () => {
            const currentQuestion = lessonMeta.qns[currentPage - 1];
            if (!currentQuestion) return;
            questionStartedAtRef.current = null;
            questionLoadStartedAtRef.current = Date.now();
            questionLoadTimeRef.current = 0;
            const folderName = `${currentQuestion.LsnCode}_E`;
            const accessToken = localStorage.getItem("access_token");
            try {
                const res = await fetch(`${url}TestQuestion/`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
                    body: JSON.stringify({ folder: folderName, qns: [currentQuestion], calledFrom: 'Test' }),
                });
                if (!res.ok) throw new Error("Failed to fetch question HTML.");
                const data = await res.json();
                const html = data.Questions[currentQuestion.QnNo];
                if (html) {
                    // Record the fetch/load interval. A loaded question is stored as at
                    // least one second so fast responses are not persisted as zero.
                    questionLoadTimeRef.current = Math.max(
                        1,
                        Math.ceil((Date.now() - questionLoadStartedAtRef.current) / 1000)
                    );
                    questionStartedAtRef.current = Date.now();
                    setCurrentQuestionHtml(html);
                }
                else throw new Error(`HTML content for question ${currentQuestion.QnNo} not found.`);
            } catch (e: any) {
                setError(e.message);
            }
        };
        fetchQuestionHtml();
    }, [currentPage, lessonMeta, url]);

    const updateSessionPoints = (pointsEarned: number) => {
        if (pointsEarned > 0) {
            try {
                const currentPoints = parseInt(sessionStorage.getItem('CurrentPoints') || '0');
                const newTotalPoints = currentPoints + pointsEarned;
                sessionStorage.setItem('CurrentPoints', newTotalPoints.toString());
                window.dispatchEvent(new Event('pointsUpdated'));
                // console.log(`Updated CurrentPoints in session: ${newTotalPoints}`);
            } catch (e) {
                console.error("Failed to update session points:", e);
            }
        }
    };

    const goToNextPage = async (isSkip: boolean = false) => {
        if (isSubmitting) return;
        let result: QuestionAnswer | SkipMetadata | null = null;
        const currentQuestionType = lessonMeta?.qns?.[currentPage - 1]?.QnType;

        if (questionComponentRef.current) {
            const skipMetadataRef = questionComponentRef.current as SkipMetadataRef;
            // A skipped objective question still needs the randomized option
            // order and option count.  Do not call getAnswer() here because
            // it validates that an option was selected.
            result = isSkip
                ? skipMetadataRef.getSkipMetadata?.() ?? null
                : questionComponentRef.current.getAnswer();
        }
        if (!isSkip && result === null) {
            return;
        }
        if (isSkip && currentQuestionType === 'O' && result === null) {
            // The question iframe has not finished initializing. Keeping the
            // Skip action disabled prevents persisting an empty OptOrder.
            return;
        }

        // console.log("Current Page:", currentPage);
        // console.log("Lesson Meta:", lessonMeta);
        if (!lessonMeta || !lessonMeta.qns || currentPage > lessonMeta.qns.length) {
            showAlert("Error: Cannot find question metadata.");
            return;
        }
        const currentQuestion = lessonMeta.qns[currentPage - 1];
        // console.log("Current Question Data:", currentQuestion);
        // console.log("Does LastQnInd exist?", currentQuestion.LastQnInd);

        const currentSessionId = lessonMeta?.sessionId;
        if (!currentSessionId) {
            showAlert("Error: Session ID is missing. Cannot save progress.");
            return;
        }

        const schoolCode = sessionStorage.getItem('SchoolCode') || '';
        const familyCode = sessionStorage.getItem('FamilyCode') || '';
        const login = sessionStorage.getItem('Login') || '';
        const chapterCode = sessionStorage.getItem('ChapterCode') || '';
        const unitCode = sessionStorage.getItem('UnitCode') || '';

        if (!schoolCode || !familyCode || !login || !currentSessionId) {
            showAlert("Error: Missing necessary session information (SchoolCode, FamilyCode, Login, SessionID). Cannot save progress.");
            return;
        }

        const isCompleting = currentPage === totalPages;
        const answerResult = result as QuestionAnswer | null;

        const payload: QuestionPerformancePayload = {
            schoolCode: schoolCode,
            familyCode: familyCode,
            login: login,
            dicCode: dicCodeParam || '',
            lsnTestCode: lsnTestCodeParam || '',
            sessionId: currentSessionId,
            lsnCode: currentQuestion.LsnCode,
            qnNo: currentQuestion.QnNo,
            subNo: currentQuestion.SubNo || 0,
            qnType: currentQuestion.QnType,
            marks: isSkip ? 0 : answerResult?.marks ?? 0,
            noOfOptions: result?.noOfOptions ?? 0,
            optOrder: result?.optOrder ?? "",
            enteredCorrectAnswer: isSkip ? "QnSkipped" : answerResult?.enteredCorrectAnswer ?? "",
            enteredWrongAnswer: isSkip ? "" : answerResult?.enteredWrongAnswer ?? "",
            enteredBlankAnswer: isSkip ? "" : answerResult?.enteredBlankAnswer ?? "",
            solutionSeen: 'N',
            clueSeen: 'N',
            principleSeen: 'N',
            questionLoadTime: getQuestionLoadTime(),
            questionAnswerTime: getQuestionAnswerTime(),
            lastQnInd: currentQuestion.LastQnInd,
            nextQnData: buildNextQuestionData(lessonMeta.qns, currentPage),
            dbgShuffledQnsOrder: buildShuffledQuestionsOrder(lessonMeta.qns),
            varValues: currentQuestion.VarValues || "",
            scoringMethod: answerResult?.scoringMethod ?? null,
            allQuestionsCompleted: isCompleting ? 'True' : 'False',
            chapterCode: chapterCode,
            unitCode: unitCode,
            hwid: hwid,
            testType: lessonMeta.testType
        };

        // console.log("Payload being sent (Using Session identifiers):", JSON.stringify(payload));

        try {
            setLoading(true);
            const data = await saveQuestionPerformance(payload);

            if (isCompleting) {
                updateSessionPoints(data.pointsEarned || 0);
                await clearCompletionCaches();
            }
        } catch (error: any) {
            console.error("Save Error:", error);
            showAlert(`There was an error saving your answer: ${error.message || 'Please try again.'}`);
            setLoading(false);
            return;
        } finally {
            if (!isCompleting) {
                setLoading(false);
            }
        }

        if (!isCompleting) {
            setCurrentQuestionHtml("");
            setCurrentPage(prev => prev + 1);
        } else {
            const calledFrom = params.get("calledFrom") || "";

            navigate(
                `/STUDENT/Tutorials/Result?DicCode=${dicCodeParam}&LsnTestCode=${lsnTestCodeParam}&SessionID=${payload.sessionId}&source=test` +
                (calledFrom ? `&calledFrom=${encodeURIComponent(calledFrom)}` : "")
            );
        }
    };

    const skipPage = () => {
        goToNextPage(true);
    };

    const renderQuestionComponent = () => {
        if (loading || !currentQuestionHtml) {
            return <tr><td><Loading /></td></tr>;
        }
        const currentQuestion = lessonMeta.qns[currentPage - 1];
        if (!currentQuestion) {
            return <tr><td>Question data not found for this page.</td></tr>;
        }

        const props = {
            lessonMeta,
            questionHtml: currentQuestionHtml,
            currentPage,
            totalPages,
            showAlert,
            correctAudio: [], 
            incorrectAudio: [],
            assetsBaseUrl,
            sessionId: lessonMeta?.sessionId,
            hwid: hwid
        };
        
        const refProps = { ...props, ref: questionComponentRef };

        let finalQnType = currentQuestion.QnType;
        

        if (finalQnType === 'O') {
            const detectedType = getQuestionTypeFromHtml(currentQuestionHtml, finalQnType);
            if (detectedType && detectedType !== 'O') {
                finalQnType = detectedType;
            }
        }

        switch (finalQnType) {
            case 'F': return <FQns {...refProps} />;
            case 'O_RADIO': return <RadioQns {...refProps} />;
            case 'O_IMAGE': return <ImageQns {...refProps} />;
            case 'O': return <OQns {...refProps} />;
            case 'W': return <WQns {...refProps} />;
            case 'D': return <DQns {...refProps} />;
            case 'B': return <BQns {...refProps} />;
            case 'N': return <NQns {...refProps} />;
            case 'I': return <IQns {...refProps} />;
            case 'C': return <CQns {...refProps} />;
            case 'M': return <MQns {...refProps} />;
            case 'L': return <LQns {...refProps} />;
            default:
                return <tr><td style={{ color: 'red', textAlign: 'center', padding: '20px' }}>
                    Unsupported question type: '{currentQuestion.QnType}'
                </td></tr>;
        }
    };
    
    const formatTime = (seconds: number): string => {
        if (seconds > 60) {
            const minutes = Math.ceil(seconds / 60);
            return `${minutes} minute${minutes > 1 ? 's' : ''} left`;
        } else {
            return `${seconds} second${seconds !== 1 ? 's' : ''} left`;
        }
    };

    if (error) return <div style={{ padding: 16, color: 'crimson', textAlign: 'center' }}>Error: {error}</div>;
    if (loading || !lessonMeta) return <Loading />;

    const answeredQuestions = Math.max(0, currentPage - 1);
    const progressPercent = totalPages > 0
        ? Math.min(100, Math.max(0, (answeredQuestions / totalPages) * 100))
        : 0;


    

const handleBack = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const calledFromParam = urlParams.get("calledFrom");

    if (window.confirm('Are you sure you want to exit ?')) {
        if (calledFromParam) {
            window.location.href = decodeURIComponent(calledFromParam);
        } else if (window.history.length > 1) {
            window.history.back();
        } else {
            navigate('/'); 
        }
    }
};




    return (
        <>
            <style>
                {`
                    tr#trQnButtons { display: none !important; }
                    #iFrameQn, .CssiFrm { height: calc(100vh - 53px) !important; }
                `}
            </style>
            <form id="form1">
                <table width="100%" border={0} cellSpacing={0} cellPadding={0}>
                    <tbody>
                        <tr>
                            <td id="tdNavHolder">
                                <table id="tblQnNav" border={0} cellPadding={0} cellSpacing={0} className="QnNavNEW">
                                    <tr className='trowT'>
                                        <td width="1%"></td>
                                        <td width="10%" align="left">
                                            <div onClick={handleBack}><img src={exit_n} alt="Exit" style={{ cursor: "pointer"}}/></div>
                                        </td>
                                        <td width="5%" align="right" className='trow2T'>
                                            <img src={timer_icon}  alt="Timer" />
                                        </td>
                                        <td width='10%' id="tdTime">
                                            <label id="lblTime">{timeLeft !== null ? formatTime(timeLeft) : '--:--'}</label>
                                        </td>
                                        <td width='15%' id="tdQnInd" align="center">
                                            Question <span id="spanQnNo">{currentPage}</span>&nbsp;of&nbsp;<span id="spanTotalQns">{totalPages}</span>
                                        </td>
                                        <td width='10%' align="right">Progress Bar:</td>
                                        <td width='28%' id="tdTimeProgress" valign="middle" align="left">
                                            <div id="divProgressBarContainer">
                                                <div id="divProgressBar" style={{ width: `${progressPercent}%` }}></div>
                                            </div>
                                        </td>
                                        <td align="left" className='trow3T'>
                                            <div style={{ cursor: "pointer" }} onClick={skipPage}><img src={skip_n} alt="Skip" /></div>
                                        </td>
                                        <td width='10%' align="right"  className='trow2T'>
                                            <div style={{ cursor: "pointer" }} onClick={() => goToNextPage(false)}><img src={next_test} alt="Next" /></div>
                                        </td>
                                        <td width="1%" align="right" id="tdOK"></td>
                                    </tr>
                                </table>
                            </td>
                        </tr>
                        
                        {renderQuestionComponent()}

                    </tbody>
                </table>
            </form>
        </>
    );
};

export default Test;
